// Angular
import {Component, OnInit} from '@angular/core';
import {Router} from '@angular/router';
import {FormBuilder, FormGroup} from '@angular/forms';
// Rxjs
import {flatMap, map, tap} from 'rxjs/operators';
// Afidium
import {Yii} from 'libraries/interfaces/Yii';
import {IOptionsCardForm, Mode} from 'libraries/module/formbuilder/forms/card-form/card-form.component';
import {CARD_OPTION_LABELS} from 'app_sasha/shared/config/wording';
import {IApiBookingSearchResponse} from 'libraries/service/resources/booking/IApiBookingSearchResponse';
import {ConfigurationResourceService} from 'libraries/service/resources/configuration/configurationResource.service';
import {
    IBookingSearchParameters,
    IBookingSearchParametersURL
} from 'libraries/interfaces/IBookingSearchParameters';
import {IProcessingSearchParameters} from 'app_sasha/shared/resources/processings/IProcessingSearchParameters';
import {ISearchStatus} from 'app_sasha/shared/interfaces/ISearchStatus';
import {BookingSearchParameters} from 'app_sasha/shared/models/BookingSearchParameters';
import {
    IDefaultSearchParametersHome,
    IQuickAccessQueries,
    DEFAULT_SEARCH_PARAMETERS_HOME
} from 'app_sasha/shared/interfaces/IDefaultSearchParametersHomeConfig';
import {BookingResourceService} from 'libraries/service/resources/booking/bookingResource.service';
import {FIELD_DESCRIPTION_LABELS} from 'app_sasha/shared/config/booking-wording';
import {IFieldSpecs} from 'libraries/module/formbuilder/fields/interfaces/field-specs';
import {BookingProcessingMenuService} from 'app_sasha/shared/services/booking-processing-menu/booking-processing-menu.service';
import {ProcessingType} from 'libraries/service/processing/processing.service';
import {SearchUtils} from 'app_sasha/shared/models/SearchUtils';
import {ErrorTracingService} from "libraries/service/error-tracing/error-tracing.service";
import {SashaEntryPoints} from "app_sasha/sasha-entry-points";

interface ISearchConfig {
    booking?: IBookingSearchParameters;
    processing?: IProcessingSearchParameters;
}

interface IPageStatus {
    lastBookings?: ISearchStatus;
    lastProcessings?: ISearchStatus;
}

@Component({
    selector: 'booking-main',
    template: `
        <card-form [options]="globalSearchForm"
                   [mode]="mode.BodyOnly"
                   (onSubmit)="onSubmit()">
            <div class="card-body-content card-padding-inside">
                <div class="col-to-row-s-tb">
                    <div class="flex-1 sep-right e2e-input-choose-booking">
                        <chips [fieldConfig]="bookingTextConfig"></chips>
                    </div>
                    <div class="sep-top">
                        <btnForm formType="submit"
                                 styleType="actionPrincipal" class="e2e-search-booking">
                            {{globalSearchForm.labels.submit}}
                        </btnForm>
                    </div>
                </div>
                <a [routerLink]="['/sasha/booking/search']"
                   [queryParams]="searchParameters.convertToUrlParameters()"
                   class="btn actionLink e2e-advanced-search">
                    {{labels.advSearch}}
                </a>
            </div>
        </card-form>

        <sasha-side-links *ngIf="isDisplayedAction"></sasha-side-links>

        <booking-saved-searched-summary
                *ngIf="quickAccessQueries" [quickAccessQueries]="quickAccessQueries"
                (onSelectProcessing)="dispatchProcessing($event)"></booking-saved-searched-summary>

        <booking-results-container
                [title]="labels.lastBookings"
                [bookings]="(lastBookingsResponse && lastBookingsResponse.booking ?
            lastBookingsResponse.booking : [])"
                [recordset]="(lastBookingsResponse && lastBookingsResponse.recordset ?
            lastBookingsResponse.recordset : [])"
                [searchStatus]="pageStatus.lastBookings"
                [searchParams]="searchBookingHomeRequest">
            <div class="booking-results-footer">
                <a [routerLink]="['/sasha/booking/search-result']"
                   [queryParams]="searchParameters.convertToUrlParameters()"
                   class="btn actionLink">
                    {{labels.seeMore}}
                </a>
            </div>
        </booking-results-container>
    `,
    styles: [`
        .container {
            display: flex;
        }

        chips ::ng-deep label {
            margin-top: 0;
        }

        a.actionLink {
            font-size: 1rem;
            padding: 0;
            margin: 0;
        }

        .sep-top {
            margin-top: 1.3rem;
        }

        .booking-results-footer,
        .processing-results-footer {
            text-align: center;
            padding: .5rem;
        }

        .advanced-search-button {
            display: block;
        }

        @media (max-width: 950px) {
            .container {
                flex-direction: column;

            .col1.flex-2.sep-right {
                margin-right: 0;
            }
        }

        }
    `]
})

export class BookingMainComponent implements OnInit {
    public mode: typeof Mode = Mode;

    public globalSearchForm: IOptionsCardForm = {
        formGroup: null,
        labels: {
            error: CARD_OPTION_LABELS.error,
            submit: CARD_OPTION_LABELS.submit,
        }
    };

    public labels: { [key: string]: string } = {
        advSearch: Yii.t('wui-sasha-js', 'Recherche avancée'),
        seeMore: Yii.t('wui-sasha-js', 'Voir plus...'),
        lastBookings: Yii.t('wui-sasha-js', 'Derniers dossiers créés'),
        lastWorks: Yii.t('wui-sasha-js', 'Derniers traitements'),
    };

    public lastBookingsResponse: IApiBookingSearchResponse;
    public pageStatus: IPageStatus;
    public searchParameters: BookingSearchParameters = new BookingSearchParameters();
    public quickAccessQueries: IQuickAccessQueries;
    public form: FormGroup;
    public bookingTextConfig: IFieldSpecs;

    public isDisplayedAction: boolean = false;

    public searchBookingHomeRequest: BookingSearchParameters;

    constructor(
        private readonly configurationResourceService: ConfigurationResourceService,
        private readonly bookingResource: BookingResourceService,
        private readonly fb: FormBuilder,
        private readonly router: Router,
        private readonly bookingProcessingMenuService: BookingProcessingMenuService,
        private readonly errorTracingService: ErrorTracingService,
    ) {
    }

    public ngOnInit(): void {
        this.form = this.fb.group({
            text: [[]]
        });

        this.globalSearchForm.formGroup = this.form;
        const urlPath: string[] = this.router.url.split('/');
        this.isDisplayedAction = urlPath[urlPath.length - 1] === SashaEntryPoints.booking;


        this.bookingTextConfig = {
            formGroup: this.form,
            label: Yii.t('wui-sasha-js', 'Recherche de dossiers'),
            formControlName: 'text',
            description: FIELD_DESCRIPTION_LABELS.text,
        };

        this.pageStatus = {
            lastBookings: {isLoading: true},
            lastProcessings: {isLoading: true},
        };

        this.configurationResourceService
            .get<IDefaultSearchParametersHome>(['wui', 'app_sasha', 'config', 'searches', 'home.json'], DEFAULT_SEARCH_PARAMETERS_HOME)
            .pipe(
                tap((configResponse: IDefaultSearchParametersHome) => this.quickAccessQueries = configResponse.quickAccessQueries),
                map((configResponse: IDefaultSearchParametersHome): IBookingSearchParameters => {
                    this.searchBookingHomeRequest = configResponse.booking as BookingSearchParameters;
                    return configResponse.booking
                }),
                flatMap((configResponse: IBookingSearchParameters) => this.bookingResource.search(configResponse)),
            )
            .subscribe(
                (response: IApiBookingSearchResponse) => {
                    this.lastBookingsResponse = response;
                },
                (error: Error) => {
                    this.pageStatus.lastBookings = getSearchStatus(this.pageStatus.lastBookings, {
                        isError: true,
                        errorMessage: error.message
                    });
                },
                () => {
                    this.pageStatus.lastBookings = getSearchStatus(this.pageStatus.lastBookings, {
                        isLoading: false,
                    });
                }
            );
    }

    /**
     * @method onSubmit
     * @description on form submit behaviour => change route to booking/search-result page with text form field argument
     */
    public onSubmit(): void {
        this.searchParameters = SearchUtils.populateSearchParametersFromForm<BookingSearchParameters>(
            this.form,
            this.searchParameters
        );

        const params: IBookingSearchParametersURL = this.searchParameters.convertToUrlParameters();

        this.router.navigate(
            ['/sasha/booking/search-result'], {
                queryParams: params
            }
        ).catch((reason: boolean) => {
            this.errorTracingService.captureException(reason);
        });
    }

    /**
     * @method changeRouteToBookingSearch
     * @description change route to acess the booking/search route with text form field value
     */
    public changeRouteToBookingSearch(): void {
        this.searchParameters = SearchUtils.populateSearchParametersFromForm<BookingSearchParameters>(
            this.form,
            this.searchParameters
        );

        const params: IBookingSearchParametersURL = this.searchParameters.convertToUrlParameters();

        this.router.navigate(
            ['/sasha/booking/search'], {
                queryParams: params
            }
        ).catch((reason: boolean) => {
            this.errorTracingService.captureException(reason);
        });
    }

    public dispatchProcessing(
        {processingName, params}: { processingName: string, params: IBookingSearchParameters }): void {

        this.bookingResource.search(params)
            .subscribe((bookingResponse: IApiBookingSearchResponse) => {
                this.bookingProcessingMenuService.changePage(processingName as ProcessingType, bookingResponse.booking);
            });
    }

}

function getSearchStatus(status: ISearchStatus, newStatus?: ISearchStatus): ISearchStatus {
    const defaultStatus: ISearchStatus = {
        isLoading: false,
        isError: false,
    };

    return {...(status || defaultStatus), ...newStatus};
}
